import { useGridContext } from 'admin/src/components/ui/DataGrid/DataGridContext';
import { parseFilterDateString } from 'admin/src/components/ui/DataGrid/filters/agGridAdapter';
import { type DataGridUrlFilterState, type DataGridUrlSortState, useDataGridUrlState } from 'admin/src/components/ui/DataGrid/useDataGridUrlState';
import { readColumnGridStateFromLocalStorage, saveColumnGridStateToLocalStorage } from 'admin/src/components/ui/DataGrid/utils';
import type { GridState, StateUpdatedEvent } from 'ag-grid-enterprise';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UrlFilterSort = {
  filter: DataGridUrlFilterState | null;
  sort: DataGridUrlSortState | null;
};

const CLEAR_SCOPE_KEY = 'all';

function normalizeDateFilterModel(model: Record<string, unknown>): Record<string, unknown> {
  if (model.filterType !== 'date') return model;

  const next = { ...model };
  for (const key of ['dateFrom', 'dateTo'] as const) {
    const value = next[key];
    if (typeof value !== 'string') continue;
    const normalized = parseFilterDateString(value);
    if (normalized) next[key] = normalized;
  }
  return next;
}

export function sanitizeUrlFilter(filter: DataGridUrlFilterState | null | undefined): DataGridUrlFilterState | null {
  if (!filter || typeof filter !== 'object') return null;

  const next: DataGridUrlFilterState = {};
  for (const [colId, model] of Object.entries(filter)) {
    if (!model || typeof model !== 'object' || Array.isArray(model)) continue;
    const values = (model as { values?: unknown[] }).values;
    if (Array.isArray(values) && values.length === 0) continue;
    next[colId] = normalizeDateFilterModel(model as Record<string, unknown>);
  }

  return Object.keys(next).length ? next : null;
}

function toUrlFilter(state: GridState): DataGridUrlFilterState | null {
  const filterModel = state.filter?.filterModel;
  if (!filterModel || !Object.keys(filterModel).length) return null;
  return sanitizeUrlFilter(filterModel as DataGridUrlFilterState);
}

function toUrlSort(state: GridState): DataGridUrlSortState | null {
  const sortModel = state.sort?.sortModel;
  if (!sortModel?.length) return null;

  return sortModel.map((item, sortIndex) => ({
    colId: item.colId,
    sort: item.sort ?? null,
    sortIndex,
  }));
}

function toGridFilterSortState(filter: DataGridUrlFilterState | null, sort: DataGridUrlSortState | null): Pick<GridState, 'filter' | 'sort'> {
  return {
    filter: {
      filterModel: filter ?? {},
    },
    sort: {
      sortModel: (sort ?? [])
        .filter((item): item is DataGridUrlSortState[number] & { sort: 'asc' | 'desc' } => item.sort === 'asc' || item.sort === 'desc')
        .map(({ colId, sort: direction }) => ({
          colId,
          sort: direction,
        })),
    },
  };
}

export function buildInitialGridState(gridId: string, filter: DataGridUrlFilterState | null, sort: DataGridUrlSortState | null): GridState {
  const columnState = readColumnGridStateFromLocalStorage(gridId);

  return {
    ...columnState,
    ...toGridFilterSortState(sanitizeUrlFilter(filter), sort),
    partialColumnState: true,
  };
}

export function useDataGridPersistedState(
  gridId: string,
  { persistState = true, columnDefsReady = true, dataReady = true, scopeKey }: { persistState?: boolean; columnDefsReady?: boolean; dataReady?: boolean; scopeKey?: string } = {},
) {
  const { gridApi } = useGridContext();
  const [{ filter, sort }, setGridUrlState] = useDataGridUrlState();
  const [urlApplyKey, setUrlApplyKey] = useState(0);
  const lastWrittenUrlRef = useRef<UrlFilterSort | null>(null);
  const pendingPopstateRef = useRef(false);
  const needsClearUrlRef = useRef(false);
  const seedFromUrlRef = useRef(true);
  const suppressUrlWritesRef = useRef(true);
  const hasMountedRef = useRef(false);
  const mountedIdentityRef = useRef<string | null>(null);
  const prevIdentityRef = useRef<{ gridId: string; scopeKey?: string } | null>(null);
  const prevDataReadyRef = useRef(dataReady);

  const identityKey = `${gridId}:${scopeKey ?? ''}`;
  const gridInstanceKey = `${identityKey}:${urlApplyKey}`;
  const identity = { gridId, scopeKey };

  if (prevIdentityRef.current === null) {
    prevIdentityRef.current = identity;
    seedFromUrlRef.current = true;
  } else if (!isEqual(prevIdentityRef.current, identity)) {
    const fromPopstate = pendingPopstateRef.current;
    const enteringClearScope = scopeKey === CLEAR_SCOPE_KEY;
    pendingPopstateRef.current = false;
    prevIdentityRef.current = identity;

    if (!hasMountedRef.current) {
      seedFromUrlRef.current = true;
      needsClearUrlRef.current = false;
    } else if (fromPopstate) {
      seedFromUrlRef.current = true;
      needsClearUrlRef.current = false;
      hasMountedRef.current = false;
      mountedIdentityRef.current = null;
    } else {
      seedFromUrlRef.current = !enteringClearScope;
      needsClearUrlRef.current = persistState && enteringClearScope;
      hasMountedRef.current = false;
      mountedIdentityRef.current = null;
    }
  }

  const baseReady = columnDefsReady && dataReady;
  if (baseReady) {
    hasMountedRef.current = true;
    mountedIdentityRef.current = identityKey;
  }

  const readyToMount = persistState ? columnDefsReady && (dataReady || mountedIdentityRef.current === identityKey) : columnDefsReady;

  useEffect(() => {
    const onPopState = () => {
      pendingPopstateRef.current = true;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: gridInstanceKey re-runs clear on remount
  useEffect(() => {
    if (!persistState || !needsClearUrlRef.current) return;

    needsClearUrlRef.current = false;
    lastWrittenUrlRef.current = { filter: null, sort: null };
    suppressUrlWritesRef.current = true;
    void setGridUrlState({ filter: null, sort: null }, { history: 'replace' });
  }, [persistState, gridInstanceKey, setGridUrlState]);

  useEffect(() => {
    if (!persistState || !pendingPopstateRef.current) return;

    const next: UrlFilterSort = { filter: sanitizeUrlFilter(filter), sort: sort ?? null };
    const lastWritten = lastWrittenUrlRef.current;

    pendingPopstateRef.current = false;

    if (lastWritten && isEqual(lastWritten.filter, next.filter) && isEqual(lastWritten.sort, next.sort)) {
      return;
    }

    // AG Grid 33 has no api.setState — remount and re-seed from URL instead.
    seedFromUrlRef.current = true;
    suppressUrlWritesRef.current = true;
    lastWrittenUrlRef.current = next;
    setUrlApplyKey((key) => key + 1);
  }, [persistState, filter, sort]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: gridInstanceKey re-arms suppress on remount
  useEffect(() => {
    if (!persistState || !readyToMount) return;

    const timeoutId = window.setTimeout(() => {
      suppressUrlWritesRef.current = false;
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [persistState, readyToMount, gridInstanceKey]);

  useEffect(() => {
    const becameReady = dataReady && !prevDataReadyRef.current;
    prevDataReadyRef.current = dataReady;

    if (!persistState || !becameReady || !gridApi || gridApi.isDestroyed()) return;

    const nextFilter = sanitizeUrlFilter(filter);
    const nextSort = sort ?? null;
    const apiFilterModel = gridApi.getFilterModel();
    const appliedFilter = apiFilterModel && Object.keys(apiFilterModel).length ? (apiFilterModel as DataGridUrlFilterState) : null;
    const appliedSort = toUrlSort(gridApi.getState());
    const needsFilterRecovery = !isEqual(appliedFilter, nextFilter);
    const needsSortRecovery = !isEqual(appliedSort, nextSort);

    if (!needsFilterRecovery && !needsSortRecovery) return;

    suppressUrlWritesRef.current = true;
    lastWrittenUrlRef.current = { filter: nextFilter, sort: nextSort };

    if (needsFilterRecovery) {
      gridApi.setFilterModel(nextFilter);
    }

    if (needsSortRecovery) {
      gridApi.applyColumnState({
        state: (nextSort ?? []).map((item) => ({
          colId: item.colId,
          sort: item.sort,
          sortIndex: item.sortIndex,
        })),
        defaultState: { sort: null },
      });
    }
  }, [persistState, dataReady, gridApi, filter, sort]);

  // Capture URL (or empty) once per grid instance; do not re-seed when URL later changes from onStateUpdated.
  // biome-ignore lint/correctness/useExhaustiveDependencies: filter/sort intentionally omitted; gridInstanceKey forces recompute on remount
  const initialState = useMemo(() => {
    if (!persistState || !readyToMount) return undefined;

    const filterSeed = seedFromUrlRef.current ? sanitizeUrlFilter(filter) : null;
    const sortSeed = seedFromUrlRef.current ? (sort ?? null) : null;
    const seed: UrlFilterSort = { filter: filterSeed, sort: sortSeed };
    lastWrittenUrlRef.current = seed;
    suppressUrlWritesRef.current = true;

    return buildInitialGridState(gridId, filterSeed, sortSeed);
  }, [persistState, readyToMount, gridInstanceKey, gridId]);

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      if (!persistState) return;

      saveColumnGridStateToLocalStorage(gridId, event.state);

      const nextFilter = toUrlFilter(event.state);
      const nextSort = toUrlSort(event.state);
      const next: UrlFilterSort = { filter: nextFilter, sort: nextSort };
      const lastWritten = lastWrittenUrlRef.current;

      if (suppressUrlWritesRef.current) {
        if (lastWritten && isEqual(next.filter, lastWritten.filter) && isEqual(next.sort, lastWritten.sort)) {
          suppressUrlWritesRef.current = false;
        }
        return;
      }

      if (lastWritten && isEqual(next.filter, lastWritten.filter) && isEqual(next.sort, lastWritten.sort)) {
        return;
      }

      lastWrittenUrlRef.current = next;
      void setGridUrlState(
        {
          filter: nextFilter,
          sort: nextSort,
        },
        { history: 'push' },
      );
    },
    [persistState, gridId, setGridUrlState],
  );

  return {
    initialState,
    onStateUpdated,
    readyToMount,
    gridInstanceKey,
  };
}
